import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT, useI18n } from "@/context/I18nContext";
import { api, errMsg, clearToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Trash2, Mail, ShieldCheck, Eye, EyeOff, HardDrive, Scale, CreditCard, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

/* Rate-limit helpers — client-side only, complementary to backend limiting. */
const ATTEMPT_KEY = "ic_del_attempts_v1";
const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 15 * 60 * 1000;

function readAttempts() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(ATTEMPT_KEY) || "null");
    if (!raw) return { count: 0, first: 0 };
    return raw;
  } catch { return { count: 0, first: 0 }; }
}
function writeAttempts(v) { try { sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(v)); } catch {} }

function GoogleGlyph({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

function startGoogleAuth() {
  try { sessionStorage.setItem("ic_post_login_redirect", "/delete-account"); } catch {}
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href =
    `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function setHead({ title, description, lang }) {
  if (title) document.title = title;
  if (description) {
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);
  }
  if (lang) { try { document.documentElement.setAttribute("lang", lang); } catch {} }
}

export default function DeleteAccount() {
  const t = useT();
  const { lang } = useI18n();
  const nav = useNavigate();
  const { user, login, logout, isAuthenticated, initializing } = useAuth();

  useEffect(() => {
    setHead({
      title: `${t("public.delete.title")} | Inner Compass`,
      description:
        lang === "nl"
          ? "Verwijder je Inner Compass-account en persoonsgegevens. Overzicht van wat wordt verwijderd en hoe je ons kunt bereiken."
          : "Delete your Inner Compass account and personal data. Overview of what gets deleted and how to reach us.",
      lang,
    });
  }, [lang, t]);

  // Steps: 1 = prove ownership, 2 = confirm destructive action, 3 = success
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [ack1, setAck1] = useState(false);
  const [ack2, setAck2] = useState(false);
  const [typedConfirm, setTypedConfirm] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    if (!initializing && isAuthenticated) setStep(2);
  }, [initializing, isAuthenticated]);

  const [attemptTick, setAttemptTick] = useState(0);
  const lockout = useMemo(() => {
    const a = readAttempts();
    if (a.count < MAX_ATTEMPTS) return 0;
    const remaining = a.first + LOCKOUT_MS - Date.now();
    return remaining > 0 ? remaining : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptTick]);

  const onSignIn = async (e) => {
    e.preventDefault();
    setErr("");
    if (lockout > 0) { setErr(t("public.delete.error.tooMany")); return; }
    setBusy(true);
    try {
      await login({ email: email.trim(), password });
      writeAttempts({ count: 0, first: 0 });
      setStep(2);
    } catch (ex) {
      const a = readAttempts();
      const now = Date.now();
      writeAttempts({ count: a.count + 1, first: a.count === 0 ? now : a.first });
      setAttemptTick((n) => n + 1);
      setErr(t("public.delete.error.invalid"));
    } finally { setBusy(false); }
  };

  const onDelete = async () => {
    setErr("");
    setDeleteBusy(true);
    try {
      await api.delete("/me/account");
      clearToken();
      setStep(3);
    } catch (ex) {
      setErr(errMsg(ex, t("public.delete.error.deleteFailed")));
    } finally { setDeleteBusy(false); }
  };

  const CONFIRM_WORD = t("public.delete.confirmWord");
  const canDelete = ack1 && ack2 && typedConfirm.trim().toUpperCase() === CONFIRM_WORD.toUpperCase() && !deleteBusy;

  const removedItems = (t("public.delete.removed.items") || []);
  const helpEmails = (t("public.delete.helpBlock.emails") || []);

  if (initializing) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-center text-ink-500" data-testid="delete-loading">
        {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-16" data-testid="page-delete-account">
      {/* ------------------------------------------------------------- */}
      {/* Header                                                        */}
      {/* ------------------------------------------------------------- */}
      <header className="mb-8">
        <div className="inline-flex items-center gap-2 rounded-full bg-terracotta-50 text-terracotta-700 px-3 py-1 text-xs mb-3">
          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
          <span>{t("public.delete.eyebrow")}</span>
        </div>
        <h1 className="font-heading text-3xl md:text-5xl tracking-tight text-ink-900">
          {t("public.delete.title")}
        </h1>
        <p className="mt-2 text-sm text-ink-500" data-testid="delete-updated">
          {t("public.lastUpdated")}
        </p>
        <p className="mt-4 text-base md:text-lg text-ink-700 max-w-2xl">
          {t("public.delete.intro")}
        </p>
      </header>

      {/* ------------------------------------------------------------- */}
      {/* Section: In-app route + support                               */}
      {/* ------------------------------------------------------------- */}
      <section className="rounded-3xl bg-white shadow-card p-6 md:p-8 mb-6" data-testid="delete-inapp">
        <h2 className="font-heading text-xl md:text-2xl mb-3">{t("public.delete.inApp.heading")}</h2>
        <p className="text-sm md:text-base text-ink-700 leading-relaxed mb-3">
          {t("public.delete.inApp.body1")}
        </p>
        <p className="text-sm md:text-base text-ink-700 leading-relaxed">
          {t("public.delete.inApp.body2").split(/(support@innercompass\.tech|privacy@innercompass\.tech)/g).map((part, i) =>
            /@innercompass\.tech$/.test(part) ? (
              <a key={i} href={`mailto:${part}`} className="text-sage-700 underline hover:text-ink-900">{part}</a>
            ) : part
          )}
        </p>
        <div className="mt-4 rounded-2xl bg-cream-100 border border-border p-4 text-sm text-ink-700" data-testid="delete-verify-note">
          {t("public.delete.inApp.verify")}
        </div>
      </section>

      {/* ------------------------------------------------------------- */}
      {/* Section: What gets deleted                                    */}
      {/* ------------------------------------------------------------- */}
      <section className="rounded-3xl bg-white shadow-card p-6 md:p-8 mb-6" data-testid="delete-what">
        <h2 className="font-heading text-xl md:text-2xl mb-3">{t("public.delete.removed.heading")}</h2>
        <p className="text-sm md:text-base text-ink-700 leading-relaxed mb-3">
          {t("public.delete.removed.intro")}
        </p>
        <ul className="list-disc pl-5 text-sm md:text-base text-ink-700 space-y-1.5" data-testid="delete-what-list">
          {Array.isArray(removedItems) && removedItems.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </section>

      {/* ------------------------------------------------------------- */}
      {/* Section: Backups + Legal + Subscriptions                      */}
      {/* ------------------------------------------------------------- */}
      <div className="grid gap-4 md:grid-cols-2 mb-6">
        <section className="rounded-3xl bg-white shadow-card p-6" data-testid="delete-backups">
          <div className="flex items-center gap-2 mb-2">
            <HardDrive className="h-4 w-4 text-sage-600" strokeWidth={1.75} />
            <h2 className="font-heading text-lg">{t("public.delete.backups.heading")}</h2>
          </div>
          <p className="text-sm text-ink-700 leading-relaxed mb-2">{t("public.delete.backups.body1")}</p>
          <p className="text-sm text-ink-700 leading-relaxed">{t("public.delete.backups.body2")}</p>
        </section>
        <section className="rounded-3xl bg-white shadow-card p-6" data-testid="delete-legal">
          <div className="flex items-center gap-2 mb-2">
            <Scale className="h-4 w-4 text-sage-600" strokeWidth={1.75} />
            <h2 className="font-heading text-lg">{t("public.delete.legal.heading")}</h2>
          </div>
          <p className="text-sm text-ink-700 leading-relaxed">{t("public.delete.legal.body")}</p>
        </section>
      </div>

      <section className="rounded-3xl bg-white shadow-card p-6 md:p-8 mb-6" data-testid="delete-subs">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard className="h-4 w-4 text-sage-600" strokeWidth={1.75} />
          <h2 className="font-heading text-xl md:text-2xl">{t("public.delete.subs.heading")}</h2>
        </div>
        <p className="text-sm md:text-base text-ink-700 leading-relaxed mb-2">{t("public.delete.subs.body1")}</p>
        <p className="text-sm md:text-base text-ink-700 leading-relaxed">{t("public.delete.subs.body2")}</p>
      </section>

      {/* ------------------------------------------------------------- */}
      {/* Section: Functional in-page deletion flow                     */}
      {/* ------------------------------------------------------------- */}
      <div className="rounded-3xl bg-sage-50 border border-sage-100 p-5 md:p-6 mb-6" data-testid="delete-flow-intro">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-4 w-4 text-sage-600" strokeWidth={1.75} />
          <h2 className="font-heading text-lg">{t("public.delete.signin.heading")}</h2>
        </div>
        <p className="text-sm text-ink-700">{t("public.delete.signin.body")}</p>
      </div>

      {/* Step 1 — Sign in to prove ownership */}
      {step === 1 && (
        <section className="rounded-3xl bg-white shadow-card p-6 md:p-8 mb-6" data-testid="delete-step-signin">
          <Button
            type="button"
            onClick={startGoogleAuth}
            data-testid="delete-signin-google"
            variant="outline"
            className="w-full h-11 rounded-full border-border bg-white hover:bg-cream-50 gap-2 mb-4"
          >
            <GoogleGlyph />
            {t("auth.continueWithGoogle")}
          </Button>

          <div className="flex items-center gap-3 my-4 text-xs text-ink-500" aria-hidden="true">
            <div className="h-px flex-1 bg-border" />
            <span>{t("auth.orContinueWith")}</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={onSignIn} className="space-y-3" data-testid="delete-signin-form">
            <div>
              <Label>{t("common.email")}</Label>
              <Input
                type="email"
                required
                data-testid="delete-email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-xl bg-cream-50 h-11 mt-1"
              />
            </div>
            <div>
              <Label>{t("common.password")}</Label>
              <div className="relative mt-1">
                <Input
                  type={showPw ? "text" : "password"}
                  required
                  data-testid="delete-password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-xl bg-cream-50 h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="delete-pw-toggle"
                  className="absolute inset-y-0 right-2 flex items-center text-ink-500 hover:text-ink-900"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {err && (
              <div className="rounded-xl bg-terracotta-50 text-terracotta-700 text-sm p-3" data-testid="delete-signin-error">
                {err}
              </div>
            )}
            {lockout > 0 && (
              <div className="rounded-xl bg-terracotta-50 text-terracotta-700 text-sm p-3" data-testid="delete-lockout">
                {t("public.delete.error.locked")}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy || lockout > 0}
              data-testid="delete-signin-submit"
              className="w-full h-11 rounded-full bg-ink-900 hover:bg-ink-700 text-white"
            >
              {busy ? t("common.loading") : t("public.delete.signin.cta")}
            </Button>
          </form>

          <p className="text-xs text-ink-500 mt-4" data-testid="delete-signin-note">
            {t("public.delete.signin.note")}{" "}
            <Link to="/forgot-password" className="underline hover:text-ink-900">{t("auth.forgot")}</Link>
          </p>
        </section>
      )}

      {/* Step 2 — Confirm destructive action */}
      {step === 2 && (
        <section className="rounded-3xl bg-white shadow-card p-6 md:p-8 mb-6" data-testid="delete-step-confirm">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-terracotta-500" strokeWidth={1.75} />
            <h2 className="font-heading text-xl">{t("public.delete.confirm.heading")}</h2>
          </div>

          <div className="rounded-2xl bg-cream-100 border border-border p-4 text-sm text-ink-700 mb-4" data-testid="delete-identity">
            <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">{t("public.delete.confirm.signedInAs")}</div>
            <div className="font-mono text-ink-900" data-testid="delete-signed-in-email">{user?.email || "—"}</div>
          </div>

          <p className="text-sm md:text-base text-ink-700 leading-relaxed mb-4">
            {t("public.delete.confirm.body")}
          </p>

          <div className="space-y-3 mb-4" data-testid="delete-ack-list">
            <label className="flex items-start gap-3 rounded-2xl border border-border p-3 cursor-pointer">
              <Checkbox checked={ack1} onCheckedChange={(v) => setAck1(!!v)} data-testid="delete-ack1" className="mt-0.5" />
              <span className="text-sm text-ink-700">{t("public.delete.confirm.ack1")}</span>
            </label>
            <label className="flex items-start gap-3 rounded-2xl border border-border p-3 cursor-pointer">
              <Checkbox checked={ack2} onCheckedChange={(v) => setAck2(!!v)} data-testid="delete-ack2" className="mt-0.5" />
              <span className="text-sm text-ink-700">{t("public.delete.confirm.ack2")}</span>
            </label>
          </div>

          <div className="mb-4">
            <Label>{t("public.delete.confirm.typePrompt", { word: CONFIRM_WORD })}</Label>
            <Input
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
              data-testid="delete-confirm-word"
              placeholder={CONFIRM_WORD}
              className="rounded-xl bg-cream-50 h-11 mt-1 font-mono uppercase tracking-widest"
            />
          </div>

          {err && (
            <div className="rounded-xl bg-terracotta-50 text-terracotta-700 text-sm p-3 mb-3" data-testid="delete-error">
              {err}
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Button
              type="button"
              onClick={onDelete}
              disabled={!canDelete}
              data-testid="delete-confirm-submit"
              className="w-full sm:w-auto h-11 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white px-6 disabled:opacity-60"
            >
              {deleteBusy ? t("common.loading") : t("public.delete.confirm.cta")}
            </Button>
            <button
              type="button"
              onClick={async () => {
                try { await logout(); } catch {}
                setStep(1);
                setAck1(false); setAck2(false); setTypedConfirm(""); setErr("");
                toast.success(t("public.delete.cancelled"));
                nav("/", { replace: true });
              }}
              data-testid="delete-cancel"
              className="w-full sm:w-auto text-sm text-ink-500 hover:text-ink-900 underline"
            >
              {t("common.cancel")}
            </button>
          </div>
        </section>
      )}

      {/* Step 3 — Done */}
      {step === 3 && (
        <section className="rounded-3xl bg-white shadow-card p-6 md:p-8 text-center mb-6" data-testid="delete-step-done">
          <div className="w-14 h-14 rounded-full bg-sage-100 mx-auto mb-4 flex items-center justify-center">
            <ShieldCheck className="h-7 w-7 text-sage-600" strokeWidth={1.75} />
          </div>
          <h2 className="font-heading text-2xl mb-2">{t("public.delete.done.heading")}</h2>
          <p className="text-sm md:text-base text-ink-700 max-w-md mx-auto mb-4">
            {t("public.delete.done.body")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="mailto:support@innercompass.tech" data-testid="delete-done-contact" className="rounded-full bg-white border border-border px-5 py-2 text-sm inline-flex items-center gap-2 hover:bg-cream-50">
              <Mail className="h-4 w-4" /> support@innercompass.tech
            </a>
            <Link to="/" data-testid="delete-done-home" className="rounded-full bg-ink-900 text-white px-5 py-2 text-sm hover:bg-ink-700">
              {t("public.delete.done.home")}
            </Link>
          </div>
        </section>
      )}

      {/* ------------------------------------------------------------- */}
      {/* Section: Help                                                 */}
      {/* ------------------------------------------------------------- */}
      <section className="rounded-3xl bg-white shadow-card p-6 md:p-8" data-testid="delete-help-section">
        <div className="flex items-center gap-2 mb-3">
          <LifeBuoy className="h-4 w-4 text-sage-600" strokeWidth={1.75} />
          <h2 className="font-heading text-xl">{t("public.delete.helpBlock.heading")}</h2>
        </div>
        <ul className="list-disc pl-5 text-sm md:text-base text-ink-700 space-y-1.5">
          {Array.isArray(helpEmails) && helpEmails.map((e, i) => (
            <li key={i}>
              <a href={`mailto:${e}`} className="text-sage-700 underline hover:text-ink-900">{e}</a>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
